import { DEFAULT_NUM_BILLING_RETRIES, FailedBill } from "@smartrr/shared/entities/Billing";
import { IPurchaseState } from "@smartrr/shared/entities/PurchaseState";
import { shopifyGidToNumber } from "@smartrr/shared/utils/ensureShopifyGid";
import { localize, typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { useMemo } from "react";

import { useSmartrrCustomerSelector } from "@customer-app/state/typedCustomerReduxHooks";

import { useOrganization } from "../../../utils";
import { getFormattedCustomerSupportEmail } from "../Subscription/utils";

export default function useFailedPaymentBannerCopy(failedBill: FailedBill, cps: IPurchaseState) {
  const { nextBillingDate } = cps;
  const { retryCount, error } = failedBill;
  const state = useSmartrrCustomerSelector(state => state.auth);
  const organization = useOrganization();
  const orgBillingRetries = useMemo(() => organization.numBillingRetries ?? DEFAULT_NUM_BILLING_RETRIES, []);
  const customerShopifyId = state.customerRelationship?.shopifyId
    ? shopifyGidToNumber(state.customerRelationship?.shopifyId!)
    : -1;

  const contactHref = organization
    ? getFormattedCustomerSupportEmail(organization!, customerShopifyId)
    : "no-email";

  //  Removing trailing period from those error messages that have it
  const formattedErrorMsg = error ? (error.endsWith(".") ? error.slice(0, -1) : error) : "";

  // Swapping between copy containing nextBillingDate and copy without
  const noticeCopy = localize(`banner.failedPaymentNotice${nextBillingDate ? "-with-date" : ""}`, {
    error: formattedErrorMsg,
    remainingRetries: orgBillingRetries - Math.max(0, retryCount - 1),
    orderDate: typedLocalize("utils.dates.toViewDateLong", {
      date: new Date(nextBillingDate),
    }),
  });

  const noteCopy = typedLocalize("banner.failedPaymentNote");

  return {
    contactHref,
    noticeCopy,
    noteCopy,
  };
}
